import {
  MEASURE_SERVICES,
  measureHandoff,
  type Job,
  type MeasureServiceKey,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal, Row } from "../../components/ui";
import { t } from "../../i18n";

/**
 * Measure the roof.
 *
 * Two ways, and the rep picks one: draw it themselves in iRoofing, or order a
 * report from GAF QuickMeasure and let it come back by email (ADR 0109).
 *
 * Neither publishes an API or a link that opens a particular house (ADR 0091),
 * so this is the honest version of "take me there with the customer already in
 * it": the address goes on the clipboard, the service opens in another tab, and
 * the rep pastes once instead of reading an address off one screen and typing it
 * into another.
 *
 * The customer's name copies separately rather than travelling with the address,
 * because the first thing the address is pasted into is a search box and two
 * lines in a search box finds nothing.
 *
 * Nothing about the measuring itself is recorded. It happens over there and
 * comes back the way it already did — a report dropped on the Roof Measurements
 * card, where the numbers are read off it.
 */
export function MeasureRoofModal({
  job,
  onClose,
  onHandoff,
}: {
  job: Job;
  onClose: () => void;
  /** The rep has been sent to a service — what the Draw Roof step ticks on. */
  onHandoff?: (service: MeasureServiceKey) => void;
}) {
  const [chosen, setChosen] = useState<MeasureServiceKey | null>(null);

  if (chosen === null) {
    return (
      <Modal
        title={t("Measure the roof")}
        subtitle={t(
          "Draw it yourself in iRoofing, or order a report from QuickMeasure. Either way the address goes on your clipboard and the site opens.",
        )}
        onClose={onClose}
        actions={
          <button className="btn-ghost" onClick={onClose}>
            {t("Close")}
          </button>
        }
      >
        <div className="choosegrid">
          {MEASURE_SERVICES.map((service) => (
            <button
              key={service.key}
              className="choosecard"
              onClick={() => setChosen(service.key)}
            >
              <b>{t(service.choice)}</b>
              <span>{t(service.blurb)}</span>
            </button>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <HandoffDetail
      job={job}
      service={chosen}
      onBack={() => setChosen(null)}
      onClose={onClose}
      onHandoff={onHandoff}
    />
  );
}

/** One service: what gets handed over, and the button that hands it. */
function HandoffDetail({
  job,
  service,
  onBack,
  onClose,
  onHandoff,
}: {
  job: Job;
  service: MeasureServiceKey;
  onBack: () => void;
  onClose: () => void;
  onHandoff?: ((service: MeasureServiceKey) => void) | undefined;
}) {
  const handoff = measureHandoff(job, service);
  const [copied, setCopied] = useState<"address" | "name" | null>(null);

  const copy = (what: "address" | "name") => {
    void navigator.clipboard?.writeText(
      what === "address" ? handoff.address : handoff.customerName,
    );
    setCopied(what);
  };

  return (
    <Modal
      title={t(handoff.service.choice)}
      subtitle={t(
        "The address goes on your clipboard and {service} opens. Paste it into their search, then {next}.",
        {
          service: handoff.service.name,
          next:
            service === "iroofing" ? t("draw the roof") : t("place the order"),
        },
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onBack}>
            {t("Back")}
          </button>
          <button
            className="btn-primary"
            disabled={handoff.blocked !== null}
            onClick={() => {
              copy("address");
              window.open(handoff.url, "_blank", "noopener,noreferrer");
              onHandoff?.(service);
            }}
          >
            {t(handoff.service.action)}
          </button>
        </>
      }
    >
      {handoff.blocked ? (
        <p className="na-hint">{handoff.blocked}</p>
      ) : (
        <>
          <Row label={t("Address")}>{handoff.address}</Row>
          <Row label={t("Customer")}>{handoff.customerName || "—"}</Row>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 12,
            }}
          >
            <button className="btn-ghost" onClick={() => copy("address")}>
              {copied === "address" ? t("Copied!") : t("Copy address")}
            </button>
            {handoff.customerName && (
              <button className="btn-ghost" onClick={() => copy("name")}>
                {copied === "name" ? t("Copied!") : t("Copy customer name")}
              </button>
            )}
          </div>

          <p className="na-hint" style={{ marginTop: 14 }}>
            {t(handoff.service.returns)}
          </p>
        </>
      )}
    </Modal>
  );
}
